import { CONFIG } from "./config";
import type { Answers, Variant } from "./types";

/**
 * サーバーへ送るリード。
 * ※ functions/src/lead.ts に同じ定義があります。片方を変えたら両方直してください。
 */
export interface LeadPayload {
  variant: Variant;
  intent: string;
  license: string;
  at: string;
  quals: string[];
  status: string;
  prefer: string[];
  zip: string;
  zipAddr: string;
  area: string;
  gender: string;
  lastName: string;
  firstName: string;
  birth: string;
  age: string;
  tel: string;
  agreed: boolean;
  agreedAt: string;
  /** 電話番号のSHA-256。生番号を照合に使わずに重複配信を防ぐためのキー */
  leadKey: string;
  source: string;
  utmMedium: string;
  utmCampaign: string;
  submittedAt: string;
  pageUrl: string;
}

/**
 * 重複排除キー：電話番号のSHA-256。
 * 提携社への配信ログ側にはこのキーだけ持たせれば、生番号を持ち回らずに
 * 30日以内の重複課金を防げます。crypto.subtle が使えない環境では空文字。
 */
export async function leadKey(tel: string): Promise<string> {
  const d = (tel || "").replace(/[^\d]/g, "");
  if (!d) return "";
  try {
    const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(d));
    return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    return "";
  }
}

export function age(iso: string): string {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  const t = new Date();
  let a = t.getFullYear() - y;
  if (t.getMonth() + 1 < m || (t.getMonth() + 1 === m && t.getDate() < d)) a--;
  return String(a);
}

export async function buildPayload(a: Answers, variant: Variant): Promise<LeadPayload> {
  const params = new URLSearchParams(location.search);
  const now = new Date().toISOString();
  return {
    variant,
    intent: a.intent,
    license: a.license,
    at: a.at,
    quals: a.quals,
    status: a.status,
    prefer: a.prefer,
    zip: a.zip,
    zipAddr: a.zipAddr,
    area: a.area,
    gender: a.gender,
    lastName: a.last,
    firstName: a.first,
    birth: a.birth,
    age: age(a.birth),
    tel: a.tel,
    agreed: a.agree,
    agreedAt: a.agree ? now : "",
    leadKey: await leadKey(a.tel),
    source: params.get("utm_source") || document.referrer || "direct",
    utmMedium: params.get("utm_medium") || "",
    utmCampaign: params.get("utm_campaign") || "",
    submittedAt: now,
    pageUrl: location.href,
  };
}

/** リードをAPIへ送信します。2xx 以外はエラーとして扱い、再送を促します */
export async function sendLead(payload: LeadPayload): Promise<void> {
  const res = await fetch(CONFIG.endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`lead submit failed: ${res.status}`);
}
